import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import {
  getConfigMultipliers,
  type IkuMultiplierMap,
} from "../api/get-config-multipliers";
import { putConfigMultipliers } from "../api/put-config-multipliers";

const JABATAN_KEYS = ["TP", "AA", "L", "LK", "GB"];
const IKU_KEYS = [
  "IKU 1",
  "IKU 2",
  "IKU 3",
  "IKU 4",
  "IKU 5",
  "IKU 6",
  "IKU 7",
  "IKU 8",
  "IKU 9",
];

export function ConfigMultiplierIkuDialog() {
  const [open, setOpen] = useState(false);
  const [multipliers, setMultipliers] = useState<IkuMultiplierMap>({});
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["config-multipliers"],
    queryFn: getConfigMultipliers,
    enabled: open,
  });

  useEffect(() => {
    if (data) {
      setMultipliers(JSON.parse(JSON.stringify(data))); // deep copy
    } else {
      // Default initialization if no data
      const defaultData: IkuMultiplierMap = {};
      IKU_KEYS.forEach((iku) => {
        defaultData[iku] = {};
        JABATAN_KEYS.forEach((jab) => {
          defaultData[iku][jab] = 0;
        });
      });
      setMultipliers(defaultData);
    }
  }, [data, open]);

  const mutation = useMutation({
    mutationFn: putConfigMultipliers,
    onSuccess: () => {
      toast.success("Konfigurasi multiplier IKU berhasil disimpan");
      queryClient.invalidateQueries({ queryKey: ["config-multipliers"] });
      setOpen(false);
    },
    onError: () => {
      toast.error("Gagal menyimpan konfigurasi multiplier IKU");
    },
  });

  const handleSave = () => {
    const payload: any = {};
    Object.keys(multipliers).forEach((iku) => {
      payload[iku] = {};
      Object.keys(multipliers[iku]).forEach((jab) => {
        const val = multipliers[iku][jab] as any;
        payload[iku][jab] = val === "" ? 0 : Number(val);
      });
    });
    mutation.mutate(payload);
  };

  const handleInputChange = (iku: string, jab: string, val: string) => {
    if (val !== "" && !/^\d*\.?\d*$/.test(val)) return;
    setMultipliers((prev: any) => ({
      ...prev,
      [iku]: {
        ...prev[iku],
        [jab]: val,
      },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="font-semibold shadow-sm">
          <Settings2 className="w-4 h-4 mr-2" />
          Config Multiplier IKU
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Konfigurasi Multiplier IKU</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-100 text-slate-700 font-semibold border-b">
                  <tr>
                    <th className="p-3 border-r w-24 text-center">IKU</th>
                    {JABATAN_KEYS.map((jab) => (
                      <th key={jab} className="p-3 border-r text-center">
                        {jab}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {IKU_KEYS.map((iku) => (
                    <tr
                      key={iku}
                      className="border-b hover:bg-slate-50 transition-colors"
                    >
                      <td className="p-3 border-r font-medium bg-slate-50 text-center">
                        {iku}
                      </td>
                      {JABATAN_KEYS.map((jab) => (
                        <td key={jab} className="p-2 border-r">
                          <Input
                            type="text"
                            inputMode="decimal"
                            className="w-full min-w-25 h-8 text-center"
                            value={multipliers[iku]?.[jab] ?? ""}
                            onChange={(e) =>
                              handleInputChange(iku, jab, e.target.value)
                            }
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-xs text-muted-foreground pt-4">
            Nilai ini merepresentasikan persentase bobot setiap indikator IKU
            berdasarkan jabatan. Contoh: 0.1 berarti 10%.
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Batal
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending || isLoading}
          >
            {mutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Simpan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
